import { Button, Checkbox, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label } from '@1trading/react';
import { ListFilter, X } from 'lucide-react';
import { type FormEvent, useState } from 'react';

export type FilterType = 'multi-text' | 'multi-choice';

export interface FilterConfig<TRow> {
  key: string;
  title: string;
  type: FilterType;
  /** Raw cell value used for matching and multi-choice option derivation */
  getValue: (row: TRow) => unknown;
}

/** column key → selected values (multi-text stores a single-entry array) */
export type FilterState = Record<string, string[]>;

const isBlank = (value: unknown): boolean => value === null || value === undefined || value === '';

// Legacy shared-table semantics: matching is EXACT equality against the
// cell value — multi-text is NOT a substring match. Array cells match on any element.
export const rowMatchesFilters = <TRow,>(row: TRow, configs: Array<FilterConfig<TRow>>, state: FilterState): boolean =>
  configs.every((config) => {
    const selected = state[config.key];
    if (!selected || selected.length === 0) return true;
    const value = config.getValue(row);
    const cellValues = Array.isArray(value) ? value : [value];
    return cellValues.some((cellValue) => selected.includes(String(cellValue)));
  });

export const applyFilters = <TRow,>(rows: TRow[], configs: Array<FilterConfig<TRow>>, state: FilterState): TRow[] => rows.filter((row) => rowMatchesFilters(row, configs, state));

/** multi-choice options come from distinct data values, arrays flattened, sorted */
export const deriveFilterOptions = <TRow,>(rows: TRow[], config: FilterConfig<TRow>): string[] => {
  const values = new Set<string>();
  for (const row of rows) {
    const value = config.getValue(row);
    for (const entry of Array.isArray(value) ? value : [value]) {
      if (!isBlank(entry)) values.add(String(entry));
    }
  }
  return [...values].sort();
};

interface FilterEditDialogProps {
  title: string;
  type: FilterType;
  /** multi-choice only */
  options: string[];
  initial: string[];
  onApply: (values: string[]) => void;
  onClose: () => void;
}

const FilterEditDialog = ({ title, type, options, initial, onApply, onClose }: FilterEditDialogProps) => {
  const [text, setText] = useState(initial[0] ?? '');
  const [selection, setSelection] = useState<string[]>(initial);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (type === 'multi-text') {
      const trimmed = text.trim();
      onApply(trimmed ? [trimmed] : []);
      return;
    }
    onApply(selection);
  };

  const toggleOption = (option: string, isChecked: boolean) => setSelection((current) => (isChecked ? [...current, option] : current.filter((entry) => entry !== option)));

  return (
    <Dialog onOpenChange={(open) => (open ? undefined : onClose())} open>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Filter by {title}</DialogTitle>
        </DialogHeader>
        <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
          {type === 'multi-text' ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="table-filter-value">{title}</Label>
              <Input id="table-filter-value" onChange={(event) => setText(event.target.value)} placeholder="Exact value" value={text} />
              <p className="text-xs text-muted-foreground">Matches the exact cell value (legacy behaviour).</p>
            </div>
          ) : options.length === 0 ? (
            <p className="text-sm text-muted-foreground">No values available to filter on.</p>
          ) : (
            <div className="flex max-h-64 flex-col gap-2 overflow-y-auto">
              {options.map((option) => (
                <div className="flex items-center gap-2" key={option}>
                  <Checkbox checked={selection.includes(option)} id={`filter-option-${option}`} onCheckedChange={(checked) => toggleOption(option, checked === true)} />
                  <Label className="break-all font-normal" htmlFor={`filter-option-${option}`}>
                    {option}
                  </Label>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => onApply([])} type="button" variant="ghost">
              Clear
            </Button>
            <Button onClick={onClose} type="button" variant="outline">
              Cancel
            </Button>
            <Button type="submit" variant="primary">
              Apply
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
FilterEditDialog.displayName = 'FilterEditDialog';

interface TableFiltersProps<TRow> {
  configs: Array<FilterConfig<TRow>>;
  rows: TRow[];
  state: FilterState;
  onChange: (state: FilterState) => void;
}

export const TableFilters = <TRow,>({ configs, rows, state, onChange }: TableFiltersProps<TRow>) => {
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const editing = configs.find((config) => config.key === editingKey) ?? null;
  const hasActiveFilters = Object.values(state).some((values) => values.length > 0);

  const applyFilter = (key: string, values: string[]) => {
    const { [key]: _removed, ...rest } = state;
    onChange(values.length > 0 ? { ...rest, [key]: values } : rest);
    setEditingKey(null);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
        <ListFilter className="size-4" />
        Filters
      </span>
      {configs.map((config) => {
        const count = state[config.key]?.length ?? 0;
        return (
          <Button className="rounded-full" key={config.key} onClick={() => setEditingKey(config.key)} size="sm" type="button" variant={count > 0 ? 'default' : 'outline'}>
            {config.title}
            {count > 0 ? <span className="rounded-full bg-background/20 px-1.5 text-xs tabular-nums">{count}</span> : null}
          </Button>
        );
      })}
      {hasActiveFilters ? (
        <Button className="rounded-full gap-1.5" onClick={() => onChange({})} size="sm" type="button" variant="ghost">
          <X className="size-3.5" />
          Clear All
        </Button>
      ) : null}
      {editing ? (
        <FilterEditDialog
          initial={state[editing.key] ?? []}
          key={editing.key}
          onApply={(values) => applyFilter(editing.key, values)}
          onClose={() => setEditingKey(null)}
          options={editing.type === 'multi-choice' ? deriveFilterOptions(rows, editing) : []}
          title={editing.title}
          type={editing.type}
        />
      ) : null}
    </div>
  );
};
TableFilters.displayName = 'TableFilters';
